import { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import { Progress, Table, TableColumn } from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Bump,
  ServiceDeployments,
  componentsOf,
  PROMOTABLE_ENVIRONMENTS,
  PromotableEnvironment,
  nextReleaseStep,
  parseImageTag,
  earlierReleases,
  environmentLabel,
  releasePermissionFor,
  type ReleaseAction,
  releasePromotePermission,
  releasePromoteProdPermission,
  releaseRollbackPermission,
  releaseRollbackProdPermission,
  unitFor,
  type ReleaseState,
  type ReleaseStep,
} from '@internal/backstage-plugin-release-common';
import { useBuildStatus } from './useBuildStatus';
import { useReleaseStates } from './useReleaseStates';

export type { ReleaseAction };

export interface ReleasePlanEntry {
  service: string;
  component: string;
  from: string;
  to: string;
  rebuilds: boolean;
  /** What promoting does to get there. Absent on a rollback. */
  step?: Exclude<ReleaseStep['kind'], 'none'>;
}

export interface ReleasePlan {
  action: ReleaseAction;
  environment: PromotableEnvironment;
  bump: Bump;
  /** Resolve and report without pushing anything. Defaults on: the task still
   * runs, logs and is audited, it just stops short of the tag. */
  dryRun: boolean;
  entries: ReleasePlanEntry[];
}

interface Props {
  action: ReleaseAction | null;
  services: ServiceDeployments[];
  /** Bumped when the operator refreshes, so the plan is not built from tags
   * cached before they pushed one. */
  generation?: number;
  onClose: () => void;
  onConfirm: (plan: ReleasePlan) => void;
}

function versionIn(
  service: ServiceDeployments,
  environment: string,
  component: string,
): string | null {
  const unit = unitFor(service, environment, component);
  if (!unit) return null;
  return parseImageTag(unit.imageTag)?.version ?? unit.imageTag;
}

/**
 * One line per workload, per component.
 *
 * Each component computes its own step from its own release line — there is no
 * fleet version, and production currently holds five release lines at once. A
 * single version box for N components would be wrong.
 */
function buildPromotePlan(
  services: ServiceDeployments[],
  states: Record<string, ReleaseState>,
  environment: PromotableEnvironment,
  bump: Bump,
): { entries: ReleasePlanEntry[]; skipped: string[] } {
  const entries: ReleasePlanEntry[] = [];
  const skipped: string[] = [];

  for (const service of services) {
    const step = nextReleaseStep(
      states[service.service] ?? { tags: [], line: null },
      environment,
      bump,
    );
    if (step.kind === 'none') {
      skipped.push(`${service.service}: ${step.reason}`);
      continue;
    }

    for (const component of componentsOf(service)) {
      const from = versionIn(service, environment, component);
      if (from === step.to) {
        skipped.push(`${service.service}/${component}: already at ${step.to}`);
        continue;
      }
      entries.push({
        service: service.service,
        component,
        from: from ?? '—',
        to: step.to,
        rebuilds: service.rebuildsPerEnvironment,
        step: step.kind,
      });
    }
  }

  return { entries, skipped };
}

/** Identifies one row of the plan, so a chosen version survives a re-render
 * without being confused with another component's. */
const unitKey = (service: string, component: string) =>
  `${service}/${component}`;

function buildRollbackPlan(
  services: ServiceDeployments[],
  tagsByService: Record<string, string[]>,
  overrides: Record<string, string>,
  environment: PromotableEnvironment,
): { entries: ReleasePlanEntry[]; skipped: string[] } {
  const entries: ReleasePlanEntry[] = [];
  const skipped: string[] = [];

  for (const service of services) {
    const tags = tagsByService[service.service] ?? [];
    for (const component of componentsOf(service)) {
      const from = versionIn(service, environment, component);
      const options = from ? earlierReleases(tags, from) : [];
      // An override only counts if it is still one of the offered versions.
      // Choices survive the dialog closing, and a component's tags move on.
      const chosen = overrides[unitKey(service.service, component)];
      const to =
        chosen && options.includes(chosen) ? chosen : (options[0] ?? null);
      if (!from || !to) {
        skipped.push(`${service.service}/${component}: no earlier release`);
        continue;
      }
      entries.push({
        service: service.service,
        component,
        from,
        to,
        rebuilds: service.rebuildsPerEnvironment,
      });
    }
  }

  return { entries, skipped };
}

function planColumns({
  action,
  tagsByService,
  onChoose,
}: {
  action: ReleaseAction;
  tagsByService: Record<string, string[]>;
  onChoose: (key: string, version: string) => void;
}): TableColumn<ReleasePlanEntry>[] {
  return [
    { title: 'Component', field: 'service' },
    { title: 'Workload', field: 'component', width: '90px' },
    { title: 'From', field: 'from' },
    {
      title: 'To',
      field: 'to',
      render: entry => {
        // Rolling back offers every earlier release rather than assuming the
        // one immediately before: the version that broke and the version you
        // want back are not always neighbours. A promotion has one correct
        // answer, derived from the tags, so it stays fixed.
        const choices =
          action === 'rollback'
            ? earlierReleases(tagsByService[entry.service] ?? [], entry.from)
            : [];
        return (
          <>
            {choices.length > 1 ? (
              <Select
                value={entry.to}
                onChange={event =>
                  onChoose(
                    unitKey(entry.service, entry.component),
                    event.target.value as string,
                  )
                }
                inputProps={{
                  'aria-label': `Version for ${entry.service} ${entry.component}`,
                }}
              >
                {choices.map(version => (
                  <MenuItem key={version} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <strong>{entry.to}</strong>
            )}
            {entry.rebuilds && (
              <Typography variant="body2" color="textSecondary" component="span">
                {'  '}rebuilt
              </Typography>
            )}
          </>
        );
      },
    },
  ];
}

function PlanTable({
  entries,
  skipped,
  columns,
}: {
  entries: ReleasePlanEntry[];
  skipped: string[];
  columns: TableColumn<ReleasePlanEntry>[];
}) {
  if (entries.length === 0) {
    // The reasons live in `skipped`, and they are the whole answer here —
    // "already at 1.0.3" and "no version to derive" call for different things.
    // Claiming nothing would change while hiding them was the worse message.
    return (
      <Typography variant="body2" color="textSecondary">
        {skipped.length > 0
          ? skipped.join('; ')
          : 'Nothing to do — no component resolved to a target.'}
      </Typography>
    );
  }

  return (
    <>
      {/* The same table the page uses, so the preview reads as the same thing
          the operator was already looking at. */}
      <Table
        options={{
          search: false,
          paging: false,
          toolbar: false,
          padding: 'dense',
        }}
        columns={columns}
        data={entries}
      />
      {skipped.length > 0 && (
        <Box pt={1}>
          <Typography variant="body2" color="textSecondary">
            {skipped.length} skipped: {skipped.join('; ')}
          </Typography>
        </Box>
      )}
    </>
  );
}

export const ReleaseDialog = ({
  action,
  services,
  generation = 0,
  onClose,
  onConfirm,
}: Props) => {
  const [environment, setEnvironment] =
    useState<PromotableEnvironment>('staging');
  const [bump, setBump] = useState<Bump>('patch');
  // Off by default. It was on while the flow was being proved out, when every
  // run was a test; now that the chain runs end to end, defaulting to a
  // rehearsal makes the ordinary case take two attempts. What guards a real
  // release is the permission and the plan shown above, not this box.
  const [dryRun, setDryRun] = useState(false);
  // Which version each rollback row targets, when the operator picked one
  // instead of taking the release immediately before.
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // All four are asked for unconditionally — hooks cannot be called behind a
  // condition — and the open action picks which one applies.
  const canPromote = usePermission({
    permission: releasePromotePermission,
  }).allowed;
  const canRollback = usePermission({
    permission: releaseRollbackPermission,
  }).allowed;
  const canPromoteProd = usePermission({
    permission: releasePromoteProdPermission,
  }).allowed;
  const canRollbackProd = usePermission({
    permission: releaseRollbackProdPermission,
  }).allowed;
  // Only the environments the chosen components actually deploy to. The fleet
  // gets staging and production; the platform's own tooling gets mgmt and
  // would otherwise be offered a staging it does not have.
  const present = new Set(
    services.flatMap(service => service.units.map(unit => unit.environment)),
  );
  const targets = PROMOTABLE_ENVIRONMENTS.filter(e => present.has(e));
  // Keep the selection honest when the chosen components change under it.
  const target = targets.includes(environment) ? environment : targets[0];

  const granted: Record<string, boolean> = {
    [releasePromotePermission.name]: canPromote,
    [releasePromoteProdPermission.name]: canPromoteProd,
    [releaseRollbackPermission.name]: canRollback,
    [releaseRollbackProdPermission.name]: canRollbackProd,
  };
  const required = releasePermissionFor(action ?? 'promote', target);
  const allowed = granted[required.name];

  const open = Boolean(action) && services.length > 0;
  const builds = useBuildStatus(
    open ? services.map(service => service.service) : [],
    generation,
  );

  const states = useReleaseStates(
    open ? services.map(s => s.service) : [],
    generation,
  );
  const tagsByService: Record<string, string[]> =
    states.kind === 'loaded'
      ? Object.fromEntries(
          Object.entries(states.byService).map(([service, state]) => [
            service,
            state.tags,
          ]),
        )
      : {};

  let plan: { entries: ReleasePlanEntry[]; skipped: string[] } | null = null;
  if (states.kind === 'loaded') {
    plan =
      action === 'rollback'
        ? buildRollbackPlan(services, tagsByService, overrides, target)
        : buildPromotePlan(services, states.byService, target, bump);
  }
  const steps = new Set(plan?.entries.map(entry => entry.step));

  // Only what the portal is sure about. Not knowing is common and must not
  // block a release the action would have allowed. And only for what builds
  // from a commit: a release pull request ships the candidate already built.
  const unbuilt = services
    .filter(service =>
      plan?.entries.some(
        entry => entry.service === service.service && entry.step !== 'release',
      ),
    )
    .map(service => ({ service: service.service, ...builds[service.service] }))
    .filter(entry => entry.verdict?.known && !entry.verdict.built);

  const count = `${services.length} component${services.length === 1 ? '' : 's'}`;
  // The target belongs in the title when there is only one of it: the radio
  // below is hidden then, and a release that does not say where it lands is
  // worse than a control nobody can change.
  // The increment is only read when a line is cut. Continuing an open line,
  // releasing it, or rolling back all have their version already determined,
  // and offering a choice that changes nothing invites someone to think it did.
  const incrementMatters = action === 'promote' && steps.has('cut');

  const where = environmentLabel(target);
  const title =
    action === 'rollback'
      ? `Rollback ${count} in ${where}`
      : `Promote ${count} to ${where}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {action === 'promote' && (
          <Box display="flex" gridGap={32} pb={2} flexWrap="wrap">
            <FormControl
              component="fieldset"
              style={{ display: targets.length > 1 ? undefined : 'none' }}
            >
              <FormLabel component="legend">Target</FormLabel>
              <RadioGroup
                row
                value={target}
                onChange={e =>
                  setEnvironment(e.target.value as PromotableEnvironment)
                }
              >
                {targets.map(option => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio size="small" />}
                    label={environmentLabel(option)}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {/* Hidden unless it changes the answer — see incrementMatters. */}
            {incrementMatters && (
              <FormControl component="fieldset">
                <FormLabel component="legend">Increment</FormLabel>
                <RadioGroup
                  row
                  value={bump}
                  onChange={e => setBump(e.target.value as Bump)}
                >
                  <FormControlLabel
                    value="patch"
                    control={<Radio size="small" />}
                    label="Patch"
                  />
                  <FormControlLabel
                    value="minor"
                    control={<Radio size="small" />}
                    label="Minor"
                  />
                  <FormControlLabel
                    value="major"
                    control={<Radio size="small" />}
                    label="Major"
                  />
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        )}

        {states.kind === 'loading' && <Progress />}
        {states.kind === 'error' && (
          <Typography variant="body2" color="error">
            Could not read the release lines: {states.error.message}
          </Typography>
        )}
        {unbuilt.length > 0 && (
          <Box pb={2}>
            <Typography variant="body2" color="error">
              <strong>Nothing to promote yet.</strong>{' '}
              {unbuilt
                .map(
                  entry =>
                    `${entry.service} has no image built for ${
                      entry.sha?.slice(0, 7) ?? 'its latest commit'
                    }`,
                )
                .join('; ')}
              . A commit that changes nothing inside the image is not built, so
              the pipeline would have nothing to retag.
            </Typography>
          </Box>
        )}

        {!allowed && (
          <Box pb={2}>
            <Typography variant="body2" color="error">
              You do not have permission to {action ?? 'promote'} {where}. This
              needs <strong>{required.name}</strong>.
            </Typography>
          </Box>
        )}

        {action === 'promote' && steps.has('cut') && (
          <Box pb={2}>
            <Typography variant="body2" color="textSecondary">
              No release line is open, so this cuts one: <code>release-cut</code>{' '}
              branches <code>release/X.Y.Z</code> from <code>dev</code> and tags
              its first candidate.
              {target === 'mgmt' &&
                ' That candidate deploys nowhere — come back to release it once it has built.'}
            </Typography>
          </Box>
        )}

        {action === 'promote' && steps.has('release') && (
          <Box pb={2}>
            <Typography variant="body2" color="textSecondary">
              This opens the <code>release/X.Y.Z</code> → <code>main</code> pull
              request. Merging it with a merge commit is what releases:{' '}
              <code>release-finish</code> tags the version, and the {where}{' '}
              gitops pull request follows.
            </Typography>
          </Box>
        )}

        {plan && (
          <PlanTable
            entries={plan.entries}
            skipped={plan.skipped}
            columns={planColumns({
              action: action ?? 'promote',
              tagsByService,
              onChoose: (key, version) =>
                setOverrides(current => ({ ...current, [key]: version })),
            })}
          />
        )}

        {action === 'rollback' && (
          <Box pt={2}>
            <Typography variant="body2" color="textSecondary">
              Nothing is tagged. The version being returned to was released
              once, so its image is already in the registry — only the overlay
              moves, as a pull request you merge.
            </Typography>
            {/* The bounded contexts migrate forward-only; the platform's own
                tooling on mgmt carries no schema. */}
            {target !== 'mgmt' && (
              <Box pt={1}>
                {/* The one thing PR #117 got exactly right, kept verbatim. */}
                <Typography variant="body2" color="error">
                  The schema is not rolled back. Migrations are forward-only,
                  so an older image may run against a newer schema. Confirm the
                  two versions are schema-compatible before relying on this.
                </Typography>
              </Box>
            )}
          </Box>
        )}

      </DialogContent>

      {/* Padded to match DialogContent, so the checkbox lines up with the
          left edge of the table above and the buttons with its right. The
          default action padding is 8px, which left the buttons hanging past
          the content. */}
      <DialogActions style={{ padding: '12px 24px' }}>
        <Box flexGrow={1}>
          <FormControlLabel
            // The checkbox carries 9px of its own padding — SwitchBase, at
            // every size — so a block starting at the content edge draws its
            // square 9px inside it. MUI's own -11 default is tuned for lining
            // up with text; this lines up with the table above.
            style={{ marginLeft: -9 }}
            control={
              <Checkbox
                size="small"
                checked={dryRun}
                onChange={event => setDryRun(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="textSecondary">
                Dry run — nothing is pushed
              </Typography>
            }
          />
        </Box>
        <Button onClick={onClose}>Cancel</Button>
        {/* Outlined while it is a dry run: available, but visibly not the
            committing action. Filled grey read as disabled next to a plain
            Cancel. */}
        <Button
          variant={dryRun ? 'outlined' : 'contained'}
          color="primary"
          disabled={
            !allowed ||
            // A rollback pushes nothing, so a commit with no image behind it
            // is only a reason to stop a promotion.
            (action === 'promote' && unbuilt.length > 0) ||
            !plan ||
            plan.entries.length === 0
          }
          onClick={() =>
            action &&
            plan &&
            onConfirm({
              action,
              environment: target,
              bump,
              dryRun,
              entries: plan.entries,
            })
          }
        >
          {dryRun ? 'Run dry' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
